// Page PUBLIQUE d'un formulaire joint à un dossier : route /formulaire-public/:jeton (sans compte, sans ProtectedRoute,
// sans localStorage, sans onboarding). Le client arrive par le lien reçu par e-mail ; le jeton (48 hex) est le secret.
// Backend consommé : « VisaTravelApp backend/formulairesPublics/routes.js » via ../formulairePublic/formulairesPublicsServices
// (plan §5.2). Visibilité des sections / questions et validation LOCALES par ../formulairePublic/visibilite (copie du
// moteur), recalculées sur les valeurs en cours de saisie ; le backend reste l'autorité (recalcul à chaque écriture,
// 422 à la soumission).
//
// États : chargement | erreur { 404 lien inconnu, 410 lien inactif, 503 indisponible, null réseau } | formulaire
// (une section visible par écran, Stepper) | enregistre (« revenez avec le même lien ») | soumis (récapitulatif).
// Écritures explicites (décision 10) : Précédent / Suivant / « Enregistrer et reprendre plus tard » = PUT reponses ;
// « Envoyer le formulaire » = validation locale puis POST soumettre (enregistre + valide en une requête).
// Verrou optimiste `version_reponses` : adoptée après chaque écriture (sauvegarde, upload, retrait, 422) ; sur un
// 409 `version`, la saisie locale de l'étape affichée est conservée, le reste vient de l'état distant.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, AlertColor, Box, Button, CircularProgress, Container, Step, StepLabel, Stepper, Typography, useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useSnackbar } from '../components/SnackbarProvider';
import QuestionRendu from '../formulairePublic/QuestionRendu';
import Recapitulatif from '../formulairePublic/Recapitulatif';
import { charger, normaliserErreur, sauvegarder, soumettre } from '../formulairePublic/formulairesPublicsServices';
import { manquantes, nettoyer, questionsVisibles, valider } from '../formulairePublic/visibilite';
import type { ErreurReponse } from '../formulairePublic/visibilite';
import type {
  Definition, ErreurPublique, EtatEcriture, FichierReponse, FormulaireCharge, Reponses, Section, ValeurReponse,
} from '../formulairePublic/types';
import { FOND_VERRE, GRADIENT_VERT, GRADIENT_VERT_HOVER, OMBRE_BOUTON, OMBRE_BOUTON_HOVER, RAYON, VERT } from '../onboarding/tokens';

// ---------------------------------------------------------------------------
// Types locaux
// ---------------------------------------------------------------------------

type Valeurs = Record<string, ValeurReponse>;
type Fichiers = Record<string, FichierReponse[]>;

// Statut d'erreur affiché : 404 lien inconnu, 410 lien inactif, 503 indisponible (toute autre réponse serveur),
// null = pas de réponse (réseau, backend arrêté)
type StatutErreur = 404 | 410 | 503 | null;

type Etat =
  | { mode: 'chargement' }
  | { mode: 'erreur'; status: StatutErreur }
  | { mode: 'formulaire' }
  | { mode: 'enregistre' }
  | { mode: 'soumis'; reponses: Reponses; date_soumission: string | null };

interface Alerte { severity: AlertColor; message: string }

const REGEX_JETON = /^[a-f0-9]{48}$/;

const MSG_CONFLIT = 'Vos réponses ont été modifiées ailleurs (autre onglet ?) : vérifiez cette étape puis enregistrez à nouveau';
const MSG_INDISPONIBLE = 'Formulaire temporairement indisponible, réessayez dans quelques minutes';

// Style du bouton principal (même grammaire que PageIntrouvable / LoginScreen, sans ThemeProvider)
const STYLE_BOUTON_PRINCIPAL = {
  py: 1.25,
  px: 3,
  borderRadius: RAYON,
  textTransform: 'none' as const,
  fontWeight: 600,
  background: GRADIENT_VERT,
  boxShadow: OMBRE_BOUTON,
  '&:hover': { background: GRADIENT_VERT_HOVER, boxShadow: OMBRE_BOUTON_HOVER },
  '&.Mui-disabled': { background: '#c8e6c9', color: '#fff', boxShadow: 'none' },
};
const STYLE_BOUTON_SECONDAIRE = {
  py: 1.25,
  px: 3,
  borderRadius: RAYON,
  textTransform: 'none' as const,
  fontWeight: 600,
  color: VERT,
  borderColor: VERT,
  '&:hover': { borderColor: VERT, backgroundColor: 'rgba(46,125,50,0.06)' },
};

// ---------------------------------------------------------------------------
// Helpers purs
// ---------------------------------------------------------------------------

const estObjet = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

// Listes de fichiers d'un `reponses.fichiers` (JSON de confiance, tableaux seulement)
const fichiersPropres = (source: unknown): Fichiers => {
  const res: Fichiers = {};
  if (!estObjet(source)) return res;
  Object.keys(source).forEach((id) => {
    const liste = source[id];
    if (Array.isArray(liste)) res[id] = liste.filter((f): f is FichierReponse => estObjet(f) && typeof f.nom === 'string');
  });
  return res;
};

// Erreur reçue d'un service (déjà normalisée : ErreurPublique) ou inattendue -> ErreurPublique. Tolère une
// ErreurPublique sans `message` (mock) : le message est alors vide et l'écran affiche ses propres libellés.
const enErreur = (e: unknown): ErreurPublique => {
  if (estObjet(e) && (typeof e.status === 'number' || e.status === null) && !('response' in e) && !('isAxiosError' in e)) {
    return { ...(e as unknown as ErreurPublique), message: typeof e.message === 'string' ? e.message : '' };
  }
  return normaliserErreur(e);
};

// Statut d'une erreur -> statut d'écran d'erreur (404 / 410 tels quels ; réseau null ; tout le reste 503)
const statutEcran = (status: number | null): StatutErreur => {
  if (status === 404 || status === 410) return status;
  if (status === null) return null;
  return 503;
};

// ISO -> « jj/mm/aaaa » (heure locale) ; chaîne vide si la date est invalide
const formaterDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// « Visa Monde — Dossier n° X — Prénom Nom — Pays » (parties vides omises)
const ligneDossier = (f: FormulaireCharge): string => {
  const d = f.dossier || { prenom: '', nom: '', pays: '', id_demande: 0 };
  const parties = ['Visa Monde'];
  if (d.id_demande) parties.push(`Dossier n° ${d.id_demande}`);
  const nom = `${d.prenom || ''} ${d.nom || ''}`.trim();
  if (nom) parties.push(nom);
  if (d.pays) parties.push(d.pays);
  return parties.join(' — ');
};

// « N réponses sont à corriger … » selon le nombre d'erreurs visibles et leur présence sur d'autres étapes
const messageErreurs = (n: number, ailleurs: boolean): string => {
  const base = n === 1 ? 'Une réponse est à corriger' : `${n} réponses sont à corriger`;
  if (ailleurs) return `${base} (y compris sur d'autres étapes : voir les champs signalés).`;
  return n === 1 ? `${base} : voir le champ signalé.` : `${base} : voir les champs signalés.`;
};

// Réponses vides (statut soumis sans `reponses`, cas théorique) pour rendre un récapitulatif sans planter
const reponsesVides = (): Reponses => ({
  valeurs: {}, fichiers: {}, visibles: [], sections_visibles: [], manquantes: [], section_courante: null, calcule_le: '',
});

// ---------------------------------------------------------------------------
// Écran plein « message » (chargement, erreurs, enregistré) — même grammaire que PageIntrouvable
// ---------------------------------------------------------------------------

interface EcranMessageProps {
  icone: React.ReactNode;
  titre: string;
  texte: React.ReactNode;
  actions?: React.ReactNode;
}

const EcranMessage: React.FC<EcranMessageProps> = ({ icone, titre, texte, actions }) => (
  <Box
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      py: 6, px: 2, gap: 0,
    }}
  >
    <Box
      sx={{
        width: 88, height: 88, borderRadius: '50%', backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center',
        justifyContent: 'center', mb: 3,
      }}
    >
      {icone}
    </Box>
    <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#333', mb: 1.5 }}>{titre}</Typography>
    <Typography variant="body1" sx={{ color: '#666', maxWidth: 420, lineHeight: 1.6, mb: actions ? 3 : 0 }}>{texte}</Typography>
    {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>{actions}</Box>}
  </Box>
);

const BoutonContact: React.FC = () => (
  <Button variant="text" href="mailto:contact@visamonde.com" sx={{ textTransform: 'none', color: '#666', fontWeight: 500 }}>
    Écrire à contact@visamonde.com
  </Button>
);

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

const FormulairePublicScreen: React.FC = () => {
  const params = useParams<{ jeton: string }>();
  const jeton = params.jeton || '';
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [etat, setEtat] = useState<Etat>({ mode: 'chargement' });
  // Données de l'instance (définition, dossier, nom du modèle) — posées par le chargement
  const [charge, setCharge] = useState<FormulaireCharge | null>(null);
  const [valeurs, setValeurs] = useState<Valeurs>({});
  const [fichiers, setFichiers] = useState<Fichiers>({});
  const [version, setVersion] = useState(0);
  // Section affichée (id) ; null = première section visible
  const [sectionCourante, setSectionCourante] = useState<string | null>(null);
  // Erreurs par question (validation locale ou 422), effacées champ par champ à la saisie
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [alerte, setAlerte] = useState<Alerte | null>(null);
  // Saisie non enregistrée (beforeunload) ; écriture en cours (boutons et champs désactivés)
  const [modifie, setModifie] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [transferts, setTransferts] = useState(0); // zones de dépôt en cours d'envoi / de retrait
  const bloque = occupe || transferts > 0;       // navigation et envoi gelés pendant un transfert
  // Question vers laquelle défiler (première erreur) ; `defilement` incrémenté pour déclencher ce défilement
  const defilerVers = useRef<string | null>(null);
  const [defilement, setDefilement] = useState(0);

  // Page hors index et sans fuite du jeton par le referrer (le lien contient le secret) : balises posées au montage,
  // retirées au démontage (le reste de l'application n'en a pas besoin)
  useEffect(() => {
    const robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex');
    const referrer = document.createElement('meta');
    referrer.setAttribute('name', 'referrer');
    referrer.setAttribute('content', 'no-referrer');
    document.head.appendChild(robots);
    document.head.appendChild(referrer);
    return () => {
      robots.remove();
      referrer.remove();
    };
  }, []);

  // Titre de l'onglet pendant la page, restauré au démontage
  useEffect(() => {
    const precedent = document.title;
    document.title = charge && charge.nom_modele ? `${charge.nom_modele} — Visa Monde` : 'Formulaire — Visa Monde';
    return () => { document.title = precedent; };
  }, [charge]);

  // Chargement (et « Réessayer ») : GET /:jeton -> formulaire ou récapitulatif ; 404 / 410 / 503 / réseau -> écran d'erreur
  const chargerFormulaire = useCallback(async () => {
    if (!REGEX_JETON.test(jeton)) {
      setEtat({ mode: 'erreur', status: 404 });
      return;
    }
    setEtat({ mode: 'chargement' });
    setAlerte(null);
    setErreurs({});
    try {
      const f = await charger(jeton);
      const def: Definition = f.definition;
      setCharge(f);
      setVersion(typeof f.version_reponses === 'number' ? f.version_reponses : 0);
      setValeurs(nettoyer(def, f.reponses ? f.reponses.valeurs : null));
      setFichiers(fichiersPropres(f.reponses ? f.reponses.fichiers : null));
      setModifie(false);
      if (f.statut === 'soumis') {
        setEtat({ mode: 'soumis', reponses: f.reponses || reponsesVides(), date_soumission: f.date_soumission });
        return;
      }
      setSectionCourante(f.reponses && typeof f.reponses.section_courante === 'string' ? f.reponses.section_courante : null);
      setEtat({ mode: 'formulaire' });
    } catch (e) {
      setEtat({ mode: 'erreur', status: statutEcran(enErreur(e).status) });
    }
  }, [jeton]);

  useEffect(() => {
    void chargerFormulaire();
  }, [chargerFormulaire]);

  // Saisie non enregistrée : confirmation du navigateur avant de quitter (patron FormIndeECard), retirée dès que tout
  // est enregistré ou soumis
  useEffect(() => {
    if (!modifie || etat.mode !== 'formulaire') return undefined;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modifie, etat.mode]);

  // ---------------------------------------------------------------------------
  // Visibilité locale (valeurs et fichiers en cours de saisie)
  // ---------------------------------------------------------------------------

  const definition: Definition | null = charge ? charge.definition : null;

  const { visibles, sectionsVisibles } = useMemo(
    () => (definition ? questionsVisibles(definition, valeurs, fichiers) : { visibles: [], sectionsVisibles: [] }),
    [definition, valeurs, fichiers]
  );
  const visiblesSet = useMemo(() => new Set(visibles), [visibles]);
  const nbManquantes = useMemo(
    () => (definition ? manquantes(definition, valeurs, fichiers, visibles).length : 0),
    [definition, valeurs, fichiers, visibles]
  );

  // Sections visibles dans l'ordre du document : une par étape
  const etapes: Section[] = useMemo(() => {
    if (!definition || !Array.isArray(definition.sections)) return [];
    const set = new Set(sectionsVisibles);
    return definition.sections.filter((s) => s && set.has(s.id));
  }, [definition, sectionsVisibles]);

  // Étape affichée : la section courante si elle est visible, sinon la section visible la plus proche dans l'ordre du
  // document (à égalité, la précédente) ; 0 sans section courante
  const indexEtape = useMemo(() => {
    if (!etapes.length || !definition || sectionCourante === null) return 0;
    const direct = etapes.findIndex((s) => s.id === sectionCourante);
    if (direct >= 0) return direct;
    const position = definition.sections.findIndex((s) => s && s.id === sectionCourante);
    if (position < 0) return 0;
    let meilleur = 0;
    let distance = Infinity;
    etapes.forEach((s, k) => {
      const d = Math.abs(definition.sections.indexOf(s) - position);
      if (d < distance) { distance = d; meilleur = k; }
    });
    return meilleur;
  }, [etapes, sectionCourante, definition]);

  const etape: Section | undefined = etapes[indexEtape];
  const derniere = indexEtape >= etapes.length - 1;
  // Seules les erreurs de questions encore visibles comptent
  const nbErreurs = Object.keys(erreurs).filter((id) => visiblesSet.has(id)).length;
  const erreursAilleurs = etapes.some((s, k) => k !== indexEtape && s.questions.some((q) => visiblesSet.has(q.id) && erreurs[q.id] !== undefined));

  // Défilement : en haut à chaque changement d'étape (ou retour au formulaire), ou vers la première question en
  // erreur quand `poserErreurs` l'a demandé (`defilement`)
  useEffect(() => {
    if (etat.mode !== 'formulaire') return;
    const cible = defilerVers.current;
    defilerVers.current = null;
    try {
      const el = cible ? document.getElementById(`question-${cible}`) : null;
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0 });
    } catch (e) {
      // jsdom ou navigateur sans défilement programmatique : sans conséquence
    }
  }, [indexEtape, etat.mode, defilement]);

  // ---------------------------------------------------------------------------
  // Saisie
  // ---------------------------------------------------------------------------

  const changerValeur = (id: string, v: ValeurReponse) => {
    setValeurs((prev) => ({ ...prev, [id]: v }));
    setModifie(true);
    if (erreurs[id] !== undefined) setErreurs((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  // Dépôt / retrait d'un fichier (déjà écrit côté serveur) : liste et version adoptées, aucune sauvegarde à faire
  const changerFichiers = (id: string, liste: FichierReponse[], ecriture: EtatEcriture) => {
    setFichiers((prev) => ({ ...prev, [id]: liste }));
    if (ecriture && typeof ecriture.version_reponses === 'number') setVersion(ecriture.version_reponses);
    if (erreurs[id] !== undefined) setErreurs((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  // Erreur de ZoneDepot : refus client (taille, extension) ou erreur backend ; 410 / 503 -> écran d'erreur,
  // 409 deja_soumis -> rechargement (récapitulatif) ; le reste (404 fichier introuvable, 400, 413, réseau) -> Snackbar seul
  const erreurFichier = (message: string, erreur?: ErreurPublique) => {
    showSnackbar(message || 'Le fichier n\'a pas pu être envoyé', 'error');
    if (!erreur) return;
    if (erreur.status === 409 && erreur.code === 'deja_soumis') { void chargerFormulaire(); return; }
    if (erreur.status === 410 || erreur.status === 503) setEtat({ mode: 'erreur', status: statutEcran(erreur.status) });
  };

  // Erreurs par question -> inline + première étape (ordre du document) portant une erreur, défilement vers la
  // première question en erreur ; l'Alert « N réponses sont à corriger » est dérivée de `erreurs` au rendu
  const poserErreurs = (liste: ErreurReponse[]) => {
    const parQuestion: Record<string, string> = {};
    liste.forEach((e) => { if (parQuestion[e.question] === undefined) parQuestion[e.question] = e.message; });
    setErreurs(parQuestion);
    setAlerte(null);
    const premiere = etapes.find((s) => s.questions.some((q) => parQuestion[q.id] !== undefined));
    if (premiere) {
      setSectionCourante(premiere.id);
      const q = premiere.questions.find((x) => parQuestion[x.id] !== undefined);
      defilerVers.current = q ? q.id : null;
      setDefilement((n) => n + 1);
    }
  };

  // 409 version : saisie locale de l'étape affichée conservée (valeur locale si renseignée, sinon distante), valeurs
  // des autres étapes et fichiers pris de l'état distant (les fichiers sont toujours l'état serveur), version adoptée
  const appliquerConflit = (distant: NonNullable<ErreurPublique['etat']>) => {
    if (distant.statut === 'soumis') { void chargerFormulaire(); return; }
    const def = definition;
    const reponses = distant.reponses;
    const idsEtape = new Set((etape ? etape.questions : []).map((q) => q.id));
    const valeursDistantes = nettoyer(def, reponses ? reponses.valeurs : null);
    setValeurs((prev) => {
      const n: Valeurs = { ...valeursDistantes };
      idsEtape.forEach((id) => { if (prev[id] !== undefined) n[id] = prev[id]; });
      return n;
    });
    setFichiers(fichiersPropres(reponses ? reponses.fichiers : null));
    setVersion(distant.version_reponses);
    setAlerte({ severity: 'warning', message: MSG_CONFLIT });
  };

  // Erreur d'une sauvegarde ou d'une soumission (hors 422, traité par `envoyer`)
  const traiterErreurEcriture = (err: ErreurPublique) => {
    if (err.status === 409 && err.code === 'version' && err.etat) { appliquerConflit(err.etat); return; }
    if (err.status === 409 && err.code === 'deja_soumis') { void chargerFormulaire(); return; }
    if (err.status === 404 || err.status === 410) { setEtat({ mode: 'erreur', status: err.status }); return; }
    // 503 / réseau / autre : la saisie reste à l'écran, message et nouvel essai possible
    setAlerte({ severity: 'error', message: err.message || MSG_INDISPONIBLE });
  };

  // PUT reponses -> true si enregistré (version adoptée). `cible` = section sur laquelle le client reprendra (celle
  // de destination pour Suivant / Précédent), par défaut la section affichée
  const sauver = async (cible?: string): Promise<boolean> => {
    if (!definition) return false;
    setOccupe(true);
    setAlerte(null);
    try {
      const r = await sauvegarder(jeton, nettoyer(definition, valeurs), version, cible ?? (etape ? etape.id : null));
      if (r && typeof r.version_reponses === 'number') setVersion(r.version_reponses);
      setModifie(false);
      return true;
    } catch (e) {
      traiterErreurEcriture(enErreur(e));
      return false;
    } finally {
      setOccupe(false);
    }
  };

  const aller = (k: number) => {
    const cible = etapes[Math.max(0, Math.min(k, etapes.length - 1))];
    if (cible) setSectionCourante(cible.id);
  };

  const precedent = async () => {
    if (indexEtape === 0) return;
    if (await sauver(etapes[indexEtape - 1]?.id)) aller(indexEtape - 1);
  };

  const suivant = async () => {
    if (await sauver(etapes[indexEtape + 1]?.id)) aller(indexEtape + 1);
  };

  const enregistrerPlusTard = async () => {
    if (await sauver()) setEtat({ mode: 'enregistre' });
  };

  // « Envoyer le formulaire » : nettoyer -> visibles -> valider en local (erreurs inline sur la bonne étape), puis
  // POST soumettre ; 422 -> erreurs du backend (brouillon enregistré : version adoptée) ; 200 -> récapitulatif
  const envoyer = async () => {
    if (!definition) return;
    const propres = nettoyer(definition, valeurs);
    const vis = questionsVisibles(definition, propres, fichiers).visibles;
    const liste = valider(definition, propres, fichiers, vis);
    if (liste.length) { poserErreurs(liste); return; }
    setOccupe(true);
    setAlerte(null);
    try {
      const r = await soumettre(jeton, propres, version, etape ? etape.id : null);
      if (r && typeof r.version_reponses === 'number') setVersion(r.version_reponses);
      setModifie(false);
      setErreurs({});
      setEtat({ mode: 'soumis', reponses: r && r.reponses ? r.reponses : reponsesVides(), date_soumission: r ? r.date_soumission : null });
    } catch (e) {
      const err = enErreur(e);
      if (err.status === 422) {
        // Brouillon enregistré par le backend : version adoptée, plus rien de non sauvegardé
        if (err.ecriture && typeof err.ecriture.version_reponses === 'number') setVersion(err.ecriture.version_reponses);
        setModifie(false);
        // Erreurs sur des questions visibles localement -> inline ; sinon (divergence improbable) message global
        const connues = (err.erreurs || []).filter((x) => visiblesSet.has(x.question));
        if (connues.length) poserErreurs(connues);
        else setAlerte({ severity: 'error', message: err.message || 'Certaines réponses sont invalides ou manquantes' });
      } else {
        traiterErreurEcriture(err);
      }
    } finally {
      setOccupe(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  const enTete = charge ? (
    <Box sx={{ mb: 3, textAlign: { xs: 'center', sm: 'left' } }}>
      <Box component="img" src="/logo.png" alt="Visa Monde" sx={{ width: 160, height: 'auto', objectFit: 'contain', mb: 1.5 }} />
      <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, wordBreak: 'break-word' }}>{ligneDossier(charge)}</Typography>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#333', mt: 0.5, wordBreak: 'break-word' }}>
        {charge.nom_modele || 'Formulaire'}
      </Typography>
    </Box>
  ) : (
    <Box sx={{ mb: 2, textAlign: 'center' }}>
      <Box component="img" src="/logo.png" alt="Visa Monde" sx={{ width: 160, height: 'auto', objectFit: 'contain' }} />
    </Box>
  );

  let contenu: React.ReactNode;

  if (etat.mode === 'chargement') {
    contenu = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
        <CircularProgress sx={{ color: VERT }} />
        <Typography variant="body2" sx={{ color: '#666' }}>Chargement du formulaire…</Typography>
      </Box>
    );
  } else if (etat.mode === 'erreur') {
    if (etat.status === 404) {
      contenu = (
        <EcranMessage
          icone={<SearchOffIcon sx={{ fontSize: 44, color: VERT }} />}
          titre="Lien inconnu"
          texte="Le lien que vous avez suivi ne correspond à aucun formulaire. Vérifiez l'adresse reçue par e-mail, ou écrivez-nous."
          actions={<BoutonContact />}
        />
      );
    } else if (etat.status === 410) {
      contenu = (
        <EcranMessage
          icone={<LinkOffIcon sx={{ fontSize: 44, color: VERT }} />}
          titre="Ce lien n'est plus actif"
          texte="Ce lien n'est plus actif. Contactez contact@visamonde.com si vous devez compléter ce formulaire."
          actions={<BoutonContact />}
        />
      );
    } else {
      contenu = (
        <EcranMessage
          icone={<CloudOffOutlinedIcon sx={{ fontSize: 44, color: VERT }} />}
          titre="Formulaire indisponible"
          texte={MSG_INDISPONIBLE}
          actions={(
            <>
              <Button variant="contained" onClick={() => { void chargerFormulaire(); }} sx={STYLE_BOUTON_PRINCIPAL}>Réessayer</Button>
              <BoutonContact />
            </>
          )}
        />
      );
    }
  } else if (etat.mode === 'enregistre') {
    contenu = (
      <EcranMessage
        icone={<SaveOutlinedIcon sx={{ fontSize: 44, color: VERT }} />}
        titre="Vos réponses sont enregistrées"
        texte="Revenez avec le même lien, à tout moment : vous reprendrez le formulaire là où vous l'avez laissé."
        actions={(
          <Button variant="contained" onClick={() => setEtat({ mode: 'formulaire' })} sx={STYLE_BOUTON_PRINCIPAL}>
            Continuer maintenant
          </Button>
        )}
      />
    );
  } else if (etat.mode === 'soumis') {
    const date = formaterDate(etat.date_soumission);
    contenu = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />} sx={{ borderRadius: 2 }}>
          {date ? `Formulaire envoyé le ${date}` : 'Formulaire envoyé'}
        </Alert>
        {definition && definition.message_fin && (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{definition.message_fin}</Typography>
        )}
        <Typography variant="body2" sx={{ color: '#666' }}>
          Vos réponses ci-dessous ne sont plus modifiables. Pour une correction, écrivez à contact@visamonde.com.
        </Typography>
        {definition && <Recapitulatif definition={definition} reponses={etat.reponses} jeton={jeton} />}
      </Box>
    );
  } else if (definition && etape) {
    const titreEtape = etape.titre || etape.id;
    const questionsEtape = etape.questions.filter((q) => q && visiblesSet.has(q.id));
    contenu = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {etapes.length > 1 && (
          isMobile ? (
            <Typography variant="subtitle2" sx={{ color: VERT, fontWeight: 700 }}>
              Étape {indexEtape + 1} / {etapes.length}
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Stepper activeStep={indexEtape} alternativeLabel sx={{ minWidth: etapes.length > 4 ? etapes.length * 110 : 0 }}>
                {etapes.map((s) => (
                  <Step key={s.id}>
                    <StepLabel>{s.titre || s.id}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )
        )}

        {indexEtape === 0 && definition.intro && (
          <Typography variant="body1" sx={{ color: '#555', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{definition.intro}</Typography>
        )}

        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: '#333' }}>{titreEtape}</Typography>

        {alerte && <Alert severity={alerte.severity} sx={{ borderRadius: 2 }}>{alerte.message}</Alert>}
        {!alerte && nbErreurs > 0 && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{messageErreurs(nbErreurs, erreursAilleurs)}</Alert>
        )}

        {questionsEtape.map((q) => (
          <QuestionRendu
            key={q.id}
            jeton={jeton}
            question={q}
            valeur={valeurs[q.id]}
            fichiers={fichiers[q.id] || []}
            erreur={erreurs[q.id]}
            disabled={occupe}
            onChange={(v) => changerValeur(q.id, v)}
            onFichiers={(liste, ecriture) => changerFichiers(q.id, liste, ecriture)}
            onErreurFichier={erreurFichier}
            onTransfert={(enCours) => setTransferts((n) => Math.max(0, n + (enCours ? 1 : -1)))}
          />
        ))}
        {questionsEtape.length === 0 && (
          <Typography variant="body2" sx={{ color: '#666' }}>
            Aucune question de cette étape ne vous concerne avec les réponses actuelles.
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: '#666' }}>
          {nbManquantes === 0
            ? 'Toutes les réponses obligatoires sont renseignées.'
            : `${nbManquantes} réponse${nbManquantes > 1 ? 's' : ''} obligatoire${nbManquantes > 1 ? 's' : ''} manquante${nbManquantes > 1 ? 's' : ''} (astérisque).`}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
          {etapes.length > 1 && (
            <Button
              variant="outlined"
              onClick={() => { void precedent(); }}
              disabled={indexEtape === 0 || bloque}
              sx={{ ...STYLE_BOUTON_SECONDAIRE, flex: '1 1 140px' }}
            >
              Précédent
            </Button>
          )}
          {derniere ? (
            <Button variant="contained" onClick={() => { void envoyer(); }} disabled={bloque} sx={{ ...STYLE_BOUTON_PRINCIPAL, flex: '1 1 140px' }}>
              Envoyer le formulaire
            </Button>
          ) : (
            <Button variant="contained" onClick={() => { void suivant(); }} disabled={bloque} sx={{ ...STYLE_BOUTON_PRINCIPAL, flex: '1 1 140px' }}>
              Suivant
            </Button>
          )}
        </Box>
        <Button
          variant="text"
          fullWidth
          onClick={() => { void enregistrerPlusTard(); }}
          disabled={bloque}
          sx={{ textTransform: 'none', color: '#666', fontWeight: 500 }}
        >
          Enregistrer et reprendre plus tard
        </Button>
      </Box>
    );
  } else {
    // Formulaire sans section visible (impossible : la première section n'a jamais de condition) — garde-fou
    contenu = (
      <EcranMessage
        icone={<SearchOffIcon sx={{ fontSize: 44, color: VERT }} />}
        titre="Aucune question à afficher"
        texte="Ce formulaire ne contient aucune étape visible. Écrivez-nous pour que nous vérifiions votre dossier."
        actions={<BoutonContact />}
      />
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', py: { xs: 2, sm: 4 }, overflowX: 'hidden' }}>
      <Container maxWidth="sm">
        {enTete}
        <Box sx={{ ...FOND_VERRE, borderRadius: RAYON, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
          {contenu}
        </Box>
        <Typography variant="caption" display="block" sx={{ color: '#888', textAlign: 'center', mt: 3 }}>
          Visa Monde — vos données servent uniquement à l'instruction de votre dossier.
        </Typography>
      </Container>
    </Box>
  );
};

export default FormulairePublicScreen;
